import { useTitle } from "@/core/context/TitleContext";
import { useEffect, useState } from "react";
import {
  useCreateLimitControl,
  useDeleteLimitControl,
  useUpdateLimitControl
} from "../../modules/limit-control/application/hooks/useLimitControlQueries";
import { DataTable } from "@/components/molecules/DataTable/DataTable";
import { LimitControlEntity } from "../../modules/limit-control/domain/entities/LimitControlEntity";
import { useQueryClient } from "@tanstack/react-query";
import { FormStatusEnum } from "@/components/molecules/FormWrapper/FormWrapper.types";
import { FormHeader } from "@/components/molecules/FormHeader/FormHeader";
import { Loading } from "@/components/atoms/Loading/Loading";
import { LimitConfigForm } from "../../modules/limit-control/presentation/LimitConfigForm";
import { useLimitConfigData } from "../../modules/limit-control/application/hooks/useLimitConfigData";
import { columns, limitEvaluationOptions, LimitFormData, limitKeys } from "../../modules/limit-control/infrastructure/LimitControlConstants";

export const LimitConfigPage = () => {
  const [formStatus, setFormStatus] = useState<FormStatusEnum>(FormStatusEnum.List);
  const [limitControlSelected, setLimitControlSelected] = useState<LimitControlEntity>();
  const [limitTypeSelected, setLimitTypeSelected] = useState("0");
  const [, setFormKey] = useState(0);
  const queryClient = useQueryClient();
  const { setTitle } = useTitle();
  const {
    limits,
    limitTypes,
    subLimitTypesFiltered,
    portfolios,
    denominators,
    isLoading,
    limitsRefetch,
  } = useLimitConfigData(limitTypeSelected);

  // hooks
  const createLimitControlMutation = useCreateLimitControl();
  const updateLimitControlMutation = useUpdateLimitControl();
  const deleteLimitControlMutation = useDeleteLimitControl();

  // effects
  useEffect(() => {
    setTitle("Control de límites");
  }, []);

  useEffect(() => {
    limitControlSelected?.limitType && setLimitTypeSelected(limitControlSelected.limitType.id);
  }, [limitControlSelected]);

  const onSubmit = async (formData: LimitFormData) => {
    if (formStatus === FormStatusEnum.Add) {
      const { id, ...payload } = formData;
      await createLimitControlMutation.mutateAsync(payload);
    } else {
      await updateLimitControlMutation.mutateAsync({ id: formData.id ?? "0", ...formData });
    }

    await queryClient.invalidateQueries({ queryKey: limitKeys.all });
    setFormStatus(FormStatusEnum.List);
    setLimitControlSelected(undefined);
  };

  const onDelete = async (id: string) => {
    await deleteLimitControlMutation.mutateAsync(id);
    await queryClient.invalidateQueries({ queryKey: limitKeys.all });
    setFormStatus(FormStatusEnum.List);
    setLimitControlSelected(undefined);
  };

  const handleChangeFormStatus = (statusOrUpdater: FormStatusEnum | ((prevState: FormStatusEnum) => FormStatusEnum)) => {
    const nextStatus = typeof statusOrUpdater === 'function'
      ? (statusOrUpdater as (prev: FormStatusEnum) => FormStatusEnum)(formStatus)
      : statusOrUpdater;

    if (nextStatus === FormStatusEnum.List) {
      setLimitControlSelected(undefined);
      setLimitTypeSelected("0");
      setFormKey(prev => prev + 1);
    }

    setFormStatus(nextStatus);
  };

  if (
    isLoading ||
    createLimitControlMutation.isPending ||
    updateLimitControlMutation.isPending ||
    deleteLimitControlMutation.isPending
  ) return <Loading />;

  return (
    <>
      <FormHeader
        title="Configuración de límites"
        changeFormStatus={handleChangeFormStatus}
        formStatus={formStatus}
        selectItem={setLimitControlSelected}
        refetch={limitsRefetch}
        canAdd
      />

      {formStatus === FormStatusEnum.List ? (
        <DataTable
          data={limits}
          columns={columns}
          tableHeadClass="bg-primary-500 text-white"
          pageSize={10}
          changeFormStatus={setFormStatus}
          selectItem={setLimitControlSelected}
        />
      ) : (
        <LimitConfigForm
          onSubmit={onSubmit}
          onDelete={onDelete}
          limitTypes={limitTypes}
          subLimitTypes={subLimitTypesFiltered}
          denominators={denominators}
          portfolios={portfolios}
          limitEvaluationOptions={limitEvaluationOptions}
          setLimitTypeSelected={setLimitTypeSelected}
          formStatus={formStatus}
          limitControlSelected={limitControlSelected}
        />
      )}
    </>
  );
};
